import { Chart, ChartDataset, ChartConfiguration, TooltipItem } from 'chart.js';
import { NSpin, NTag } from 'naive-ui';
import { defineComponent, shallowRef } from 'vue';

import componentBase from '~/components/component-base';
import SlotDetailsModal from '~/components/slot-details-modal/slot-details-modal.vue';
import { config } from './chart.config';


import { mapState } from 'pinia';
import { useDashboardStore } from '~/store/dashboard.store';
import { AvoltaAlarmConfiguration } from '../../configs/alarms/avolta-alarm.config';
import moment, { Moment } from 'moment';
import { dangerColor, warningColor } from '~/variables';
import { useStationStore } from '~/store/station.store';
import { DashboardSlots } from '~/api/models/dashboard/dashboard-history.model';

export enum DatasetEnum {
    GAS_ALARM = 'GAS_ALARM',
    TROUBLE = 'TROUBLE',
    OK = 'OK',
    BOTH = "BOTH",
}

export default defineComponent({

    extends: componentBase,

    components: {
        SlotDetailsModal,

        NTag,
        NSpin,
    },

    data() {
        return {
            chart: null as Chart,
            datasets: [] as ChartDataset[],

            AvoltaAlarmConfiguration,
        }
    },

    computed: {
        ...mapState(useDashboardStore, ['since', 'before', 'history', 'measures', 'loading']),
        ...mapState(useStationStore, ['stations']),
    },

    async mounted() {
        this.init();
    },

    methods: {

        init() {
            const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;

            if (!chartElement) {
                return;
            }

            const configuration = {
                ...config,
            } as ChartConfiguration<'bar'>;
            

            // configuration.options.scales.x.ticks.autoSkip = false;
            configuration.options.scales.x.ticks.callback = (value, index) => {
                if(!this.history.length) {
                    return '';
                }

                const slot = this.history[index];
                const time = Math.floor((slot.end - slot.start) / 2 + slot.start);
                return moment.unix(time).format('ll');

            };

            configuration.options.onClick = (event, elements) => {
                if (elements[0] && this.history[elements[0].index]) {
                    //@ts-ignore
                    this.$refs?.modal.open(this.history[elements[0].index], 'alarm')
                }
            }

            configuration.options.onHover = (event, elements) => {
                //@ts-ignore
                event.native.target.style.cursor = elements[0] ? 'pointer' : 'default';
            }

            configuration.options.plugins = {
                ...configuration.options.plugins,
                tooltip: {
                    callbacks: {
                        title: (items: TooltipItem<'bar'>[]) => {

                            if(!items?.length) {
                                return '';
                            }

                            const slot: DashboardSlots = this.history[items[0].dataIndex];

                            if(!slot) {
                                return items[0].label;
                            }

                            const start: Moment = moment.unix(slot.start);

                            const format = 'LL';

                            return start.format(format);
                        }
                    }
                }
            };

            //@ts-ignore
            this.chart = shallowRef(new Chart(chartElement.getContext('2d'), configuration));
        },

        compute() {

            if (!this.history) {
                return;
            }

            this.chart.data.labels = this.history.map(slot => {
                const time = Math.floor((slot.end - slot.start) / 2 + slot.start);
                return moment.unix(time).format('l')
            });

            // @ts-ignore
            const datasets: { [key in DatasetEnum]: ChartDataset } = {};

            // datasets[DatasetEnum.OK] = ({ label: this.$t('global.ok'), data: [], backgroundColor: greenColor });
            datasets[DatasetEnum.GAS_ALARM] = ({ label: this.$t('dashboard_events_chart.datasets.gas.label'), data: [], backgroundColor: dangerColor, });
            datasets[DatasetEnum.BOTH] = ({ label: this.$t('dashboard_events_chart.datasets.both.label'), data: [], backgroundColor: this.createDiagonalPattern()  });
            datasets[DatasetEnum.TROUBLE] = ({ label: this.$t('dashboard_events_chart.datasets.trouble.label'), data: [], backgroundColor: warningColor });
            // datasets[DatasetEnum.OK] = ({ label: 'ok', data: [], backgroundColor: greenColor });

            this.history.forEach(slot => {
                // datasets[DatasetEnum.OK].data.push(slot.ok);

                const intersect = slot.gas.filter(value => slot.trouble?.includes(value));
                const gas = slot.gas?.filter((devId: string) => !intersect.includes(devId));
                const trouble = slot.trouble?.filter((devId: string) => !intersect.includes(devId));

                datasets[DatasetEnum.GAS_ALARM].data.push(gas.length || 0);
                datasets[DatasetEnum.TROUBLE].data.push(trouble?.length || 0);
                datasets[DatasetEnum.BOTH].data.push(intersect?.length || 0);
                // datasets[DatasetEnum.OK].data.push(slot.ok);
            });

            this.chart.data.datasets = Object.values(datasets);
        },

        updateChart() {

            if (!this.chart) {
                return;
            }

            try {
                this.compute();

                this.chart.update();
            } catch (e) {
                console.error(e);
                this.$notifyError(`An error occured while updating chart`, e);
            }
        },

        createDiagonalPattern(backgroundColor = dangerColor, color = warningColor) {
            // create a 10x10 px canvas for the pattern's base shape
            const shape = document.createElement('canvas')
            shape.width = 10;
            shape.height = 10;
            // get the context for drawing
            const c = shape.getContext('2d');
            
            c.fillStyle = backgroundColor;
            c.fillRect(0, 0, shape.width, shape.height);
            // draw 1st line of the shape 
            c.strokeStyle = color;
            c.lineWidth = 2;
            c.beginPath();
            c.moveTo(2, 0);
            c.lineTo(10, 8);
            c.stroke();
            // draw 2nd line of the shape 
            c.beginPath();
            c.moveTo(0, 8);
            c.lineTo(2, 10);
            c.stroke();
            // create the pattern from the shape
            return c.createPattern(shape, 'repeat')
          }

    },

    watch: {
        loading(value, old) {
            if (!value && old) {
                this.updateChart();
            }
        }
    }
});
